import React, { useCallback, useRef, useState } from "react";
import { Send } from "lucide-react";
import { useChatStore } from "./chatStore";

interface ChatInputProps {
  onSend: (message: string) => void;
}

export const ChatInput: React.FC<ChatInputProps> = ({ onSend }) => {
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const streamingPhase = useChatStore((s) => s.streamingPhase);
  const inputDisabled = isStreaming || streamingPhase === "awaiting_confirmation";

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || inputDisabled) return;
    onSend(trimmed);
    setValue("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [value, inputDisabled, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleInput = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    // Auto-resize
    const el = e.target;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, []);

  return (
    <div className="chat-input-area">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        placeholder="Type a message..."
        rows={1}
        disabled={inputDisabled}
      />
      <button
        onClick={handleSend}
        disabled={!value.trim() || inputDisabled}
        className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors"
        style={{
          background: value.trim() && !inputDisabled ? "var(--brand-badge-bg)" : "var(--bg-muted, #e5e7eb)",
          color: value.trim() && !inputDisabled ? "var(--brand-badge-text)" : "var(--text-muted)",
          cursor: value.trim() && !inputDisabled ? "pointer" : "not-allowed",
        }}
        aria-label="Send message"
      >
        <Send size={16} />
      </button>
    </div>
  );
};
